import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { CanRole, useCurrentUserProfile } from '@/lib/local-platform';
import {
  getSpeechTopics,
  getSpeechTopicById,
  createSpeechTopic,
  updateSpeechTopic,
  deleteSpeechTopic,
  getAdminSpeechTopics,
} from '@/api/speechTopic';
import { SearchFilter, ActiveFilters } from './components/SearchFilter';
import { TopicList } from './components/TopicList';
import { TopicDetailDialog } from './components/TopicDetailDialog';
import { TopicFormDialog } from './components/TopicFormDialog';
import type {
  SpeechTopic,
  SpeechTopicDetail,
  ContentCategory,
  SuitableGrade,
  CreateSpeechTopicReq,
  UpdateSpeechTopicReq,
} from '@shared/api.interface';

const SpeechTopicListPage = () => {
  const { user_id } = useCurrentUserProfile();
  const isLoggedIn = !!user_id;

  // 列表数据状态
  const [topics, setTopics] = useState<SpeechTopic[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // 筛选状态
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<ContentCategory | ''>('');
  const [grade, setGrade] = useState<SuitableGrade | ''>('');
  const [page] = useState(1);
  const [pageSize] = useState(20);

  // 弹窗状态
  const [detailTopic, setDetailTopic] = useState<SpeechTopicDetail | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTopic, setEditingTopic] = useState<SpeechTopic | null>(null);
  const [editingDownloadLink, setEditingDownloadLink] = useState('');

  // 删除确认弹窗状态
  const [deleteTopic, setDeleteTopic] = useState<SpeechTopic | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // 获取列表数据
  const fetchTopics = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getSpeechTopics({
        search: search || undefined,
        category: category || undefined,
        grade: grade || undefined,
        page,
        pageSize,
      });
      setTopics(result.items);
      setTotal(result.total);
    } catch (error) {
      toast.error('获取演讲主题列表失败');
    } finally {
      setIsLoading(false);
    }
  }, [search, category, grade, page, pageSize]);

  useEffect(() => {
    fetchTopics();
  }, [fetchTopics]);

  // 重置筛选
  const handleReset = () => {
    setSearch('');
    setCategory('');
    setGrade('');
  };

  // 查看详情
  const handleViewDetail = async (topic: SpeechTopic) => {
    try {
      const detail = await getSpeechTopicById(topic.id);
      setDetailTopic(detail);
      setIsDetailOpen(true);
    } catch (error) {
      toast.error('获取主题详情失败');
    }
  };

  // 新建主题
  const handleCreate = () => {
    if (!isLoggedIn) {
      toast.error('请先登录后再创建');
      return;
    }
    setEditingTopic(null);
    setEditingDownloadLink('');
    setIsFormOpen(true);
  };

  // 编辑主题
  const handleEdit = async (topic: SpeechTopic) => {
    if (!isLoggedIn) {
      toast.error('请先登录后再编辑');
      return;
    }
    try {
      const result = await getAdminSpeechTopics({ status: 'all' });
      const adminTopic = result.items.find((item) => item.id === topic.id);
      if (!adminTopic) {
        toast.error('获取主题编辑信息失败');
        return;
      }
      setEditingDownloadLink(adminTopic.download_link || '');
    } catch (error) {
      toast.error('获取主题编辑信息失败');
      return;
    }
    setEditingTopic(topic);
    setIsFormOpen(true);
  };

  // 提交表单
  const handleFormSubmit = async (data: CreateSpeechTopicReq | UpdateSpeechTopicReq) => {
    if (editingTopic) {
      await updateSpeechTopic(editingTopic.id, data as UpdateSpeechTopicReq);
    } else {
      await createSpeechTopic(data as CreateSpeechTopicReq);
    }
    await fetchTopics();
  };

  // 删除主题
  const handleDelete = async (topic: SpeechTopic) => {
    if (!isLoggedIn) {
      toast.error('请先登录后再删除');
      return;
    }
    setDeleteTopic(topic);
  };

  // 确认删除
  const confirmDelete = async () => {
    if (!deleteTopic) return;

    setIsDeleting(true);
    try {
      await deleteSpeechTopic(deleteTopic.id);
      toast.success('删除成功');
      setDeleteTopic(null);
      setIsFormOpen(false);
      setEditingTopic(null);
      setEditingDownloadLink('');
      await fetchTopics();
    } catch (error) {
      toast.error('删除失败');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* 页面标题和操作 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">演讲主题列表</h1>
          <p className="text-sm text-muted-foreground mt-1">
            共 {total} 个演讲主题
          </p>
        </div>
        {isLoggedIn && (
          <CanRole roles={['admin']}>
            <Button onClick={handleCreate} className="gap-2">
              <Plus className="h-4 w-4" />
              新建主题
            </Button>
          </CanRole>
        )}
      </div>

      {/* 搜索与筛选区 */}
      <SearchFilter
        search={search}
        category={category}
        grade={grade}
        resultCount={topics.length}
        onSearchChange={setSearch}
        onCategoryChange={setCategory}
        onGradeChange={setGrade}
        onReset={handleReset}
      />

      {/* 已选条件展示 */}
      <ActiveFilters
        search={search}
        category={category}
        grade={grade}
        onSearchChange={setSearch}
        onCategoryChange={setCategory}
        onGradeChange={setGrade}
        onReset={handleReset}
      />

      {/* 主题列表区 */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <TopicList
          topics={topics}
          onViewDetail={handleViewDetail}
          onEdit={isLoggedIn ? handleEdit : undefined}
        />
      )}

      {/* 详情弹窗 */}
      <TopicDetailDialog
        topic={detailTopic}
        open={isDetailOpen}
        onOpenChange={setIsDetailOpen}
      />

      {/* 表单弹窗（新建/编辑） */}
      <TopicFormDialog
        topic={editingTopic}
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        onSubmit={handleFormSubmit}
        onDelete={isLoggedIn ? handleDelete : undefined}
        downloadLink={editingDownloadLink}
      />

      {/* 删除确认弹窗 */}
      <Dialog open={!!deleteTopic} onOpenChange={(open) => !open && setDeleteTopic(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>确认删除</DialogTitle>
            <DialogDescription>
              确定要删除主题"{deleteTopic?.title}"吗？此操作不可撤销。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTopic(null)} disabled={isDeleting}>
              取消
            </Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={isDeleting}>
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              删除
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SpeechTopicListPage;
